import { Button, Input, Switch } from '@bs/ui-components'
import { Pencil } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { DialogField, EditDialog } from '@/components/ui/edit-dialog'
import { ApiError } from '@/services/http'
import { updateMyAccount } from '@/services/account'
import { useAccount } from '@/app/use-account'
import { useAuth } from '@/app/use-auth'
import { AuthApiError, changeEmail, changePassword } from '@/features/auth/api'
import { PhoneVerifyDialog } from './phone-verify-dialog'
import { getAccessToken } from '@/services/session'

/**
 * `gender` is a reference-data elId (`packages/types/src/enums.ts`'s split) whose i18n labels
 * live in `bs-core.lookup_items` — a store talent-service must never read (`database.module.ts`:
 * "this service must only ever touch models.talent"). `bs-core.lookup_items` only seeds 0-2
 * (Unknown/Male/Female) today; 3-4 extend that space for this picker's other two options — the
 * field is schema-documented as an open 0-9 elId, so this is filling it in, not inventing a
 * parallel vocabulary. A shared reference-data surface should replace this table, not grow it.
 */
const GENDER_LABELS: Record<number, string> = { 0: 'Unknown', 1: 'Male', 2: 'Female', 3: 'Other', 4: 'Prefer not to say' }
const GENDER_VALUES: Record<string, number> = { Male: 1, Female: 2, Other: 3, 'Prefer not to say': 4 }
// Schema-documented as binary ("0 hidden | 1 visible") — unlike gender, NOT an open elId space, so
// the picker's middle option has no real value to send. Real mode narrows to the two that exist.
const VISIBILITY_LABELS: Record<number, string> = { 0: 'Nobody', 1: 'All verified recruiters' }
const VISIBILITY_VALUES: Record<string, number> = { 'All verified recruiters': 1, Nobody: 0 }
const REAL_VISIBILITY_OPTIONS = ['All verified recruiters', 'Nobody']
const MOCK_VISIBILITY_OPTIONS = ['All verified recruiters', 'Recruiters from companies I applied to', 'Nobody']

type Editor =
  | 'birthDate'
  | 'govId'
  | 'securityClearance'
  | 'gender'
  | 'phone'
  | 'email'
  | 'password'
  | 'companyUpdates'
  | 'jobUpdates'
  | 'visibility'
  | 'closeAccount'
  | null

const tocItems = [
  { id: 'personal-details-section', label: 'Personal Details' },
  { id: 'account-access-section', label: 'Account Access' },
  { id: 'mail-notifications-section', label: 'Mail Notifications' },
  { id: 'privacy-section', label: 'Profile Privacy' },
  { id: 'close-account-section', label: 'Close Account' },
] as const

export function AccountSettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [editor, setEditor] = useState<Editor>(null)
  const [draft, setDraft] = useState('')
  const [passwordDraft, setPasswordDraft] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' })
  const [passwordError, setPasswordError] = useState('')
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const { account, error: accountError, setAccount } = useAccount()
  const [verifyPhoneOpen, setVerifyPhoneOpen] = useState(false)
  const [justVerified, setJustVerified] = useState(false)
  const phoneVerified = justVerified || Boolean(account?.phoneVerifiedAt)
  const { logout } = useAuth()
  const navigate = useNavigate()
  const [formError, setFormError] = useState('')

  useEffect(() => {
    const target = searchParams.get('scrollTo')
    if (!target) return
    const frame = window.requestAnimationFrame(() => scrollToSection(target))
    return () => window.cancelAnimationFrame(frame)
  }, [searchParams])

  /** Real values where `GET /v1/me` answered, blank until it does (see `accountError` for when it can't). */
  const view = {
    // Raw ISO (`YYYY-MM-DD`), not locale-formatted — this feeds straight into a native date
    // input; formatting for display happens only at the render call site.
    birthDate: account?.birthDate?.slice(0, 10) ?? '',
    governmentId: account?.govId ?? '',
    securityClearance: account?.securityClearance ?? false,
    gender: account ? GENDER_LABELS[account.gender] ?? 'Unknown' : '',
    phone: account?.phone ?? '',
    email: account?.email ?? '',
    companyUpdates: Boolean(account?.notifications.companyUpdates?.email),
    jobUpdates: Boolean(account?.notifications.jobUpdates?.email),
    visibility: account ? VISIBILITY_LABELS[account.visibility] ?? 'Nobody' : '',
    accountClosed: account?.state.isClosed ?? false,
    passwordUpdatedAt: '',
  }

  function openEditor(next: Exclude<Editor, null>) {
    const values: Record<Exclude<Editor, null>, string> = {
      birthDate: view.birthDate,
      govId: view.governmentId,
      securityClearance: view.securityClearance ? 'yes' : 'no',
      gender: view.gender,
      phone: view.phone,
      email: view.email,
      password: '',
      companyUpdates: String(view.companyUpdates),
      jobUpdates: String(view.jobUpdates),
      visibility: view.visibility,
      closeAccount: '',
    }
    setDraft(values[next])
    setFormError('')
    if (next === 'password') {
      setPasswordDraft({ oldPassword: '', newPassword: '', confirmPassword: '' })
      setPasswordError('')
    }
    setEditor(next)
  }

  async function saveEditor() {
    if (!editor) return
    if (editor === 'password') {
      if (!passwordDraft.oldPassword) return setPasswordError('Enter your current password.')
      if (passwordDraft.newPassword.length < 8) return setPasswordError('New password must contain at least 8 characters.')
      if (passwordDraft.newPassword !== passwordDraft.confirmPassword) return setPasswordError('Passwords do not match.')

      if (!account) return setPasswordError('Your account has not loaded — please try again.')

      const accessToken = getAccessToken()
      if (!accessToken) return setPasswordError('Your session expired — please log in again.')

      setIsChangingPassword(true)
      setPasswordError('')
      try {
        await changePassword(accessToken, passwordDraft.oldPassword, passwordDraft.newPassword)
      } catch (err) {
        setIsChangingPassword(false)
        return setPasswordError(
          err instanceof AuthApiError && err.status === 403
            ? 'Current password is incorrect.'
            : 'Could not change your password — please try again.',
        )
      }
      // Every session died on the backend the moment the password changed (see /auth/password's
      // own comment — a compromised-account response is what this action means, including for
      // the caller's own session). `logout()` clears the now-dead local tokens the same way; the
      // one difference is WHY, so the login page can say something more useful than a bare form.
      await logout()
      navigate('/login?passwordChanged=1', { replace: true })
      return
    }

    // The sign-in address lives in Firebase too: the gateway changes both halves and revokes the
    // sessions (legacy synced Firebase on email change), so this ends with a fresh sign-in.
    if (editor === 'email') {
      const token = getAccessToken()
      if (!token) return
      try {
        await changeEmail(token, draft.trim())
      } catch (cause) {
        setFormError(cause instanceof AuthApiError && cause.status === 409 ? 'That email address is already in use.' : 'Could not change the email address.')
        return
      }
      await logout()
      navigate('/login?emailChanged=1', { replace: true })
      return
    }

    // Nothing to write through until the account loads. The mock path this replaced made the save
    // LOOK successful while changing nothing anyone else would ever see.
    if (!account) return

    const patch: Parameters<typeof updateMyAccount>[0] = {
      ...(editor === 'birthDate' && { birthDate: draft }),
      ...(editor === 'govId' && { govId: draft }),
      ...(editor === 'securityClearance' && { securityClearance: draft === 'yes' }),
      ...(editor === 'gender' && { gender: GENDER_VALUES[draft] ?? account.gender }),
      ...(editor === 'phone' && { phone: draft }),
      ...(editor === 'companyUpdates' && { notifications: { companyUpdates: { email: draft === 'true' } } }),
      ...(editor === 'jobUpdates' && { notifications: { jobUpdates: { email: draft === 'true' } } }),
      ...(editor === 'visibility' && { visibility: VISIBILITY_VALUES[draft] ?? account.visibility }),
      ...(editor === 'closeAccount' && { accountClosed: true as const }),
    }

    try {
      const { user } = await updateMyAccount(patch)
      setAccount(user)
      setEditor(null)
    } catch (error) {
      setFormError(
        error instanceof ApiError && error.code === 'email_taken'
          ? 'That email is already in use on another account.'
          : 'Could not save — please try again.',
      )
    }
  }

  function navigateToSection(id: string) {
    setSearchParams({ scrollTo: id })
    scrollToSection(id)
  }

  return (
    <main className="min-w-full px-4 pb-5 pt-12 sm:px-8">
      <div className="mx-auto flex max-w-[1400px] items-start justify-end">
        <nav className="sticky top-24 hidden w-80 shrink-0 lg:block" aria-label="Account settings sections">
          <ul className="w-80 space-y-[30px] px-2.5 py-10">
            {tocItems.map((item) => (
              <li key={item.id}>
                <button type="button" onClick={() => navigateToSection(item.id)} className="text-lg font-normal capitalize leading-5 text-[#8a8a8a] transition hover:text-accent focus:text-accent">
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className="w-full rounded-[20px] bg-white px-4 py-8 shadow-[0_3px_3px_rgba(0,0,0,0.15)] sm:px-8 lg:w-[calc(100%-320px)] lg:px-20 lg:py-10">
          {accountError ? <p role="alert" className="mb-6 rounded-xl border border-destructive/25 bg-destructive/5 px-4 py-3 text-sm text-destructive">{accountError}</p> : null}
          <SettingsSection id="personal-details-section" title="Personal Details">
            <EditableValue label="Birthday" value={formatDate(view.birthDate) || '-'} onEdit={() => openEditor('birthDate')} />
            <EditableValue label="Government ID" value={view.governmentId || '-'} onEdit={() => openEditor('govId')} />
            <EditableValue label="Security Clearance" value={view.securityClearance ? 'yes' : 'no'} onEdit={() => openEditor('securityClearance')} />
            <EditableValue label="Gender" value={view.gender || '-'} onEdit={() => openEditor('gender')} />
          </SettingsSection>

          <SettingsSection id="account-access-section" title="Account Access">
            <EditableValue label="Phone Number" value={view.phone || '-'} onEdit={() => openEditor('phone')} />
            {/* Legacy Twilio Verify: the number's verified state, and the way to verify it. */}
            {view.phone ? (
              <div className="flex items-center justify-between gap-3 px-1 text-sm">
                <span className={phoneVerified ? 'text-accent' : 'text-muted-foreground'}>{phoneVerified ? 'Phone number verified' : 'Phone number not verified'}</span>
                {phoneVerified ? null : <Button type="button" size="sm" variant="outline" onClick={() => setVerifyPhoneOpen(true)}>Verify</Button>}
              </div>
            ) : null}
            {verifyPhoneOpen && view.phone ? <PhoneVerifyDialog phone={view.phone} onClose={() => setVerifyPhoneOpen(false)} onVerified={() => { setJustVerified(true); setVerifyPhoneOpen(false) }} /> : null}
            <EditableValue label="Email" value={view.email || '-'} onEdit={() => openEditor('email')} />
            <EditableValue label="Password" value="Change Password" onEdit={() => openEditor('password')} />
            {view.passwordUpdatedAt ? <p className="text-sm font-medium text-[#00895b]">Password changed locally on {new Date(view.passwordUpdatedAt).toLocaleString()}.</p> : null}
          </SettingsSection>

          <SettingsSection id="mail-notifications-section" title="Email Notifications">
            <EditableValue label="Get updates from companies" value={view.companyUpdates ? 'On' : 'Off'} onEdit={() => openEditor('companyUpdates')} />
            <EditableValue label="Get new job updates" value={view.jobUpdates ? 'On' : 'Off'} onEdit={() => openEditor('jobUpdates')} />
          </SettingsSection>

          <SettingsSection id="privacy-section" title="Profile Privacy">
            <EditableValue label="Visible To" value={view.visibility || '-'} onEdit={() => openEditor('visibility')} />
            {/* Honest by decision (2026-09-02): legacy wrote this field and nothing ever read it.
                The preference is kept — enforcement is a Phase-2 consent feature — but the page
                must not promise what no code holds. */}
            <p className="mt-2 text-sm text-[#8a8a8a]">This preference is saved with your account but not yet enforced.</p>
          </SettingsSection>

          <SettingsSection id="close-account-section" title="Close Account">
            <div>
              <Button type="button" variant="destructive" onClick={() => openEditor('closeAccount')}>Close Account</Button>
              {view.accountClosed ? <p className="mt-3 text-sm font-medium text-[#00895b]">{account ? 'This account is closed.' : 'Account close was simulated locally. No account data was changed.'}</p> : null}
            </div>
          </SettingsSection>
        </div>
      </div>

      {editor ? (
        <EditDialog
          title={editorTitle(editor)}
          submitLabel={editor === 'password' && isChangingPassword ? 'Changing…' : editor === 'closeAccount' ? 'Approve' : 'Save'}
          cancelLabel={editor === 'closeAccount' ? 'Decline' : 'Cancel'}
          description={
            account
              ? editor === 'password'
                ? "You'll be signed out everywhere and need to log in again with the new password."
                : 'Changes are saved to your account.'
              : 'Changes are saved only in this browser and will not update backend data.'
          }
          submitDisabled={editor === 'password' && isChangingPassword}
          onClose={() => setEditor(null)}
          onSubmit={saveEditor}
        >
          <EditorContent
            editor={editor}
            draft={draft}
            setDraft={setDraft}
            passwordDraft={passwordDraft}
            setPasswordDraft={setPasswordDraft}
            passwordError={passwordError}
            visibilityOptions={account ? REAL_VISIBILITY_OPTIONS : MOCK_VISIBILITY_OPTIONS}
          />
          {formError ? <p role="alert" className="mt-4 text-sm text-destructive">{formError}</p> : null}
        </EditDialog>
      ) : null}
    </main>
  )
}

function SettingsSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24 border-t border-[#eeeff2] pt-10 first:border-t-0 first:pt-0 [&+section]:mt-[50px]">
      <h2 className="mb-8 text-[22px] font-semibold leading-[1.3] text-accent sm:mb-[45px]">{title}</h2>
      <div className="space-y-[35px]">{children}</div>
    </section>
  )
}

function EditableValue({ label, value, onEdit }: { label: string; value: string; onEdit(): void }) {
  return (
    <div className="flex items-center justify-between gap-6">
      <div className="min-w-0">
        <p className="mb-2 text-sm font-medium text-[#555]">{label}</p>
        <p className="break-all text-base font-normal leading-5 text-[#555]">{value}</p>
      </div>
      <button type="button" onClick={onEdit} aria-label={`Edit ${label}`} className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-input text-[#555] transition hover:bg-[#ebebeb] focus:text-accent">
        <Pencil className="size-4" />
      </button>
    </div>
  )
}

function EditorContent({ editor, draft, setDraft, passwordDraft, setPasswordDraft, passwordError, visibilityOptions }: { editor: Exclude<Editor, null>; draft: string; setDraft(value: string): void; passwordDraft: { oldPassword: string; newPassword: string; confirmPassword: string }; setPasswordDraft(value: { oldPassword: string; newPassword: string; confirmPassword: string }): void; passwordError: string; visibilityOptions: string[] }) {
  if (editor === 'closeAccount') return <p className="text-base text-[#404040]">Are you sure you want to close your account?</p>
  if (editor === 'companyUpdates' || editor === 'jobUpdates') {
    const checked = draft === 'true'
    return <div className="flex items-center justify-between gap-6"><span className="text-base text-[#404040]">{editor === 'companyUpdates' ? 'Get updates from companies' : 'Get new job updates'}</span><Switch checked={checked} onClick={() => setDraft(String(!checked))} /></div>
  }
  if (editor === 'password') {
    return <div className="grid gap-5"><DialogField label="Old Password"><Input type="password" value={passwordDraft.oldPassword} onChange={(event) => setPasswordDraft({ ...passwordDraft, oldPassword: event.target.value })} placeholder="Enter old password" required /></DialogField><DialogField label="New Password"><Input type="password" value={passwordDraft.newPassword} onChange={(event) => setPasswordDraft({ ...passwordDraft, newPassword: event.target.value })} placeholder="Enter new password" required minLength={8} /></DialogField><DialogField label="Confirm Password"><Input type="password" value={passwordDraft.confirmPassword} onChange={(event) => setPasswordDraft({ ...passwordDraft, confirmPassword: event.target.value })} placeholder="Confirm new password" required minLength={8} /></DialogField>{passwordError ? <p role="alert" className="text-sm text-destructive">{passwordError}</p> : null}</div>
  }
  if (editor === 'securityClearance') return <SelectField label="Security Clearance" value={draft} onChange={setDraft} placeholder="Select Security clearance" options={['no', 'yes']} />
  if (editor === 'gender') return <SelectField label="Gender" value={draft} onChange={setDraft} placeholder="Select Gender" options={['Female', 'Male', 'Other', 'Prefer not to say']} />
  if (editor === 'visibility') return <SelectField label="Visible To" value={draft} onChange={setDraft} placeholder="Select profile visibility" options={visibilityOptions} />
  const config = {
    birthDate: { label: 'Birth Date', placeholder: 'Set birth date', type: 'date' },
    govId: { label: 'Government ID', placeholder: 'Enter Government ID', type: 'text' },
    phone: { label: 'Phone Number', placeholder: 'Enter mobile number', type: 'tel' },
    email: { label: 'Email', placeholder: 'Email', type: 'email' },
  }[editor]
  if (!config) return null
  return <DialogField label={config.label}><Input type={config.type} value={draft} onChange={(event) => setDraft(event.target.value)} placeholder={config.placeholder} required={editor === 'email'} /></DialogField>
}

function SelectField({ label, value, onChange, placeholder, options }: { label: string; value: string; onChange(value: string): void; placeholder: string; options: string[] }) {
  return <DialogField label={label}><select value={value} onChange={(event) => onChange(event.target.value)} className="min-h-11 rounded-xl border border-input-border bg-input px-[15px] py-2.5 text-base font-normal text-[#404040] outline-none transition focus:border-accent"><option value="" disabled>{placeholder}</option>{options.map((option) => <option key={option} value={option}>{option}</option>)}</select></DialogField>
}

function editorTitle(editor: Exclude<Editor, null>) {
  return ({
    birthDate: 'Edit Birth Date',
    govId: 'Edit Government ID',
    securityClearance: 'Edit Security Clearance',
    gender: 'Edit Gender',
    phone: 'Edit Phone',
    email: 'Edit Email',
    password: 'Change Password',
    companyUpdates: 'Edit companies updates',
    jobUpdates: 'Edit jobs updates',
    visibility: 'Edit Profile Visibility',
    closeAccount: 'Close Account Confirmation',
  } as const)[editor]
}

function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function formatDate(value: string | undefined): string {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString()
}
